// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Boucle de retour « cet avis est faux » — moitié manquante de S3 (25/08/2026).
//
// POURQUOI CE PANNEAU EXISTE
// --------------------------
// S0 a mesuré 84,6 % de justesse le 24/08/2026, mais surtout que le corpus ne
// produit que 11 positions défavorables — il en faudrait ~150 sans erreur pour
// soutenir « ≥ 98 % ». La contrainte n'est plus la qualité de l'extraction,
// c'est la TAILLE DE L'ÉCHANTILLON, et chaque ligne de vérité terrain se paie
// aujourd'hui en heures de Marc. Ici, chaque usage client en produit une.
//
// ⚠️ IL NE MODIFIE PAS LE RAPPORT, et le dit en toutes lettres dès l'intro. Un
// utilisateur qui croirait corriger son livrable serait trompé — et laisser
// quiconque réécrire la position d'une règle détruirait la valeur de mesure de
// la donnée : on ne saurait plus distinguer ce que le produit a conclu de ce
// qu'on lui a fait dire.
//
// ⚠️ N'AFFICHE QUE LES POSITIONS NON FAVORABLES. C'est exactement le périmètre
// de `s0_precision.csv`, et c'est ce qui garde le panneau lisible : mesuré le
// 25/08/2026 sur les 70 analyses portant des règles, une analyse en compte
// 3 en médiane et 6 au maximum. Lister les 20 règles noierait le signal sous du
// vocabulaire normatif, pour un public que le dossier décrit comme peu à l'aise
// avec l'IA. Ce que Normascan n'a PAS vu ne peut pas y figurer — aucune règle ne
// l'a produit : c'est l'objet du second formulaire, en bas.
//
// ⛔ TROIS POSITIONS, TROIS MOTS : Favorable / Réserve / Défavorable. Jamais
// « conforme », jamais « favorable avec réserve ». Les libellés passent par
// `t()` et se retrouvent tels quels dans la vérité terrain.
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { useI18n } from '../i18n';
import {
  getAnalysisPositions, createRuleFeedback, deleteRuleFeedback,
} from '../services/api';

// Les trois couleurs des positions, alignées sur `AnalysisView` et
// `AnalysisCard` : la personne doit retrouver dans le panneau la couleur qu'elle
// vient de voir sur la carte.
const TEINTE_POSITION = {
  FAVORABLE:   { bg: '#f0fdf4', color: '#16a34a', border: '#86efac' },
  RESERVE:     { bg: '#fefce8', color: '#ca8a04', border: '#fde047' },
  DEFAVORABLE: { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
};

// Doit rester d'accord avec `RETOUR_COMMENTAIRE_MIN` dans `flask_app.py`. Le
// front valide pour éviter un aller-retour inutile ; le back valide parce qu'il
// est le seul à ne pas pouvoir être contourné.
const COMMENTAIRE_MIN = 10;

const STYLE_ZONE = {
  width: '100%', padding: '8px 10px', borderRadius: 6,
  border: '1px solid #d1d5db', fontSize: 13, resize: 'vertical',
  boxSizing: 'border-box', fontFamily: 'inherit',
};

const AIDE_PAR_POSITION = (t) => ({
  FAVORABLE:   t('retour_tip_favorable'),
  RESERVE:     t('retour_tip_reserve'),
  DEFAVORABLE: t('retour_tip_defavorable'),
  INDECIDABLE: t('retour_tip_unknown'),
});

// ⛔ CE COMPOSANT DOIT RESTER AU NIVEAU DU MODULE.
//
// Il vivait dans le corps de `RetourAvisPanel` jusqu'au 25/08/2026. Chaque
// frappe appelait `setMotif`, re-rendait le parent, et **recréait un type de
// composant tout neuf** : React ne le reconnaissait pas, démontait le
// sous-arbre et le remontait. Le `<textarea>` était donc détruit puis recréé à
// chaque lettre, et le focus perdu — il fallait recliquer entre chaque
// caractère (signalé par Marc au premier test).
//
// ⚠️ Le remettre dans le corps du parent, même « pour la lisibilité »,
// ramènerait le défaut sans qu'aucun test ne le voie.
const Formulaire = ({
  avecPosition, positionRendue, onEnvoyer, placeholder,
  choix, setChoix, motif, setMotif, erreur, envoi, onAnnuler,
}) => {
  const { t } = useI18n();
  // `tente` passe à vrai au premier clic sur Valider. Tant qu'il est faux, le
  // compteur reste neutre : on informe sans réprimander quelqu'un qui écrit.
  const [tente, setTente] = useState(false);
  const longueur = motif.trim().length;
  const tropCourt = longueur < COMMENTAIRE_MIN;
  const positionManquante = avecPosition && !choix;
  const aide = AIDE_PAR_POSITION(t);

  const valider = () => {
    setTente(true);
    if (tropCourt || positionManquante) return;
    onEnvoyer();
  };

  return (
    <div style={{ marginTop: 10, padding: '12px 14px', background: '#fafafa',
                  borderRadius: 8, border: '1px solid #e5e7eb' }}>
      {avecPosition && (
        <>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
            {t('retour_expected_label')}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {[
              ['FAVORABLE',   t('retour_pos_favorable'),   t('retour_tip_favorable')],
              ['RESERVE',     t('retour_pos_reserve'),     t('retour_tip_reserve')],
              ['DEFAVORABLE', t('retour_pos_defavorable'), t('retour_tip_defavorable')],
              ['INDECIDABLE', t('retour_pos_unknown'),     t('retour_tip_unknown')],
            ]
              // ⚠️ La position DÉJÀ RENDUE est retirée des choix. Signaler qu'un
              // avis est faux en reproposant le même avis produirait une ligne
              // que `noter_s0.py` noterait « OK » — c'est-à-dire l'inverse de ce
              // que la personne a voulu dire. Le back refuse aussi ce cas.
              .filter(([valeur]) => valeur !== positionRendue)
              .map(([valeur, libelle, infobulle]) => (
                <button
                  key={valeur}
                  type="button"
                  title={infobulle}
                  onClick={() => setChoix(valeur)}
                  style={{
                    padding: '6px 12px', borderRadius: 999, fontSize: 12, cursor: 'pointer',
                    fontWeight: choix === valeur ? 700 : 500,
                    border: `1px solid ${choix === valeur ? (TEINTE_POSITION[valeur]?.color || 'var(--blue)') : (tente && positionManquante ? '#ef4444' : '#d1d5db')}`,
                    background: choix === valeur ? (TEINTE_POSITION[valeur]?.bg || '#eff6ff') : '#fff',
                    color: choix === valeur ? (TEINTE_POSITION[valeur]?.color || 'var(--blue)') : '#374151',
                  }}
                >
                  {libelle}
                </button>
              ))}
          </div>
          {choix && (
            <p style={{ fontSize: 11, color: '#6b7280', marginTop: -6, marginBottom: 10 }}>
              {aide[choix]}
            </p>
          )}
          {tente && positionManquante && (
            <p style={{ color: '#ef4444', fontSize: 12, margin: '-6px 0 10px' }}>
              {t('retour_need_position')}
            </p>
          )}
        </>
      )}

      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
        {t('retour_why_label')}
      </div>
      <textarea
        value={motif}
        onChange={e => setMotif(e.target.value)}
        placeholder={placeholder}
        rows={3}
        style={{
          ...STYLE_ZONE,
          border: `1px solid ${tente && tropCourt ? '#ef4444' : '#d1d5db'}`,
        }}
      />
      {/* Le « pourquoi » est obligatoire (Marc, 25/08/2026) : sans lui, un
          verdict n'est pas réutilisable six mois plus tard — c'est la leçon de
          la colonne `note` de la feuille S0. On l'explique plutôt que de
          simplement bloquer.
          ⚠️ Le minimum était appliqué SANS ÊTRE DIT : le bouton restait
          désactivé et cliquer ne produisait rien. Le compteur ci-dessous rend
          la règle visible avant le clic, et rouge après un clic infructueux. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12,
                    margin: '4px 0 0' }}>
        <p style={{ fontSize: 11, color: '#6b7280', margin: 0 }}>
          {t('retour_why_help')}
        </p>
        <span style={{ fontSize: 11, whiteSpace: 'nowrap',
                       color: tente && tropCourt ? '#ef4444' : '#6b7280',
                       fontWeight: tente && tropCourt ? 700 : 400 }}>
          {/* ⚠️ `t` ne prend QU'UNE CLÉ — pas d'interpolation (`i18n.js:68`).
              Les nombres se composent donc ici, autour d'un libellé traduit. */}
          {longueur} / {COMMENTAIRE_MIN} {t('retour_char_min')}
        </span>
      </div>
      {erreur && <p style={{ color: '#ef4444', fontSize: 12, marginTop: 6 }}>{erreur}</p>}
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        {/* ⚠️ Le bouton reste CLIQUABLE même incomplet. Désactivé, il ne disait
            rien : la personne cliquait dans le vide sans comprendre. */}
        <Button size="sm" onClick={valider} disabled={envoi}>
          {envoi ? t('retour_sending') : t('retour_send')}
        </Button>
        <Button size="sm" variant="outline" onClick={onAnnuler}>
          {t('retour_cancel')}
        </Button>
      </div>
    </div>
  );
};

const RetourAvisPanel = ({ analysis, onClose }) => {
  const { t } = useI18n();
  const analysisId = analysis.id || analysis.analysis_id;

  const [positions, setPositions] = useState([]);
  const [retours, setRetours]     = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur]       = useState('');

  // Formulaire ouvert : la clé d'une position (`amiante#4`), ou 'ecart_manque'.
  const [ouvert, setOuvert]       = useState(null);
  const [choix, setChoix]         = useState('');
  const [motif, setMotif]         = useState('');
  const [envoi, setEnvoi]         = useState(false);
  const [confirme, setConfirme]   = useState(false);

  const cle = (p) => `${p.norme}#${p.rule_number}`;

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const data = await getAnalysisPositions(analysisId);
      setPositions(data.positions || []);
      setRetours(data.retours || []);
      setErreur('');
    } catch (e) {
      setErreur(e.message);
    } finally {
      setChargement(false);
    }
  }, [analysisId]);

  useEffect(() => { charger(); }, [charger]);

  const retourDe = (p) => retours.find(
    r => r.kind === 'position' && r.norme === p.norme && r.rule_number === p.rule_number
  );
  const ecartsManques = retours.filter(r => r.kind === 'ecart_manque');

  const fermerFormulaire = () => { setOuvert(null); setChoix(''); setMotif(''); setErreur(''); };

  const ouvrirFormulaire = (identifiant, retourExistant) => {
    setOuvert(identifiant);
    // Rouvrir sur un retour déjà déposé le pré-remplit : on modifie son avis,
    // on ne le retape pas.
    setChoix(retourExistant?.position_attendue || '');
    setMotif(retourExistant?.commentaire || '');
    setErreur('');
  };

  const envoyer = async (charge) => {
    if (motif.trim().length < COMMENTAIRE_MIN) {
      setErreur(t('retour_why_required'));
      return;
    }
    setEnvoi(true);
    try {
      await createRuleFeedback(analysisId, { ...charge, commentaire: motif.trim() });
      fermerFormulaire();
      setConfirme(true);
      setTimeout(() => setConfirme(false), 2500);
      await charger();
    } catch (e) {
      setErreur(e.message || t('retour_error'));
    } finally {
      setEnvoi(false);
    }
  };

  const retirer = async (retourId) => {
    if (!window.confirm(t('retour_delete_confirm'))) return;
    try {
      await deleteRuleFeedback(retourId);
      await charger();
    } catch (e) {
      setErreur(e.message);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)',
               display: 'flex', justifyContent: 'flex-end' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ width: '100%', maxWidth: 560, background: '#fff', height: '100%',
                    display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.18)' }}>

        {/* En-tête */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex',
                      alignItems: 'center', justifyContent: 'space-between', background: '#fff' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--black)' }}>{t('retour_title')}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {analysis.filename}
            </div>
          </div>
          <button onClick={onClose}
                  style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>
            ✕
          </button>
        </div>

        <div style={{ flex: 1, padding: '16px 20px', overflowY: 'auto' }}>

          {/* Ce que fait — et surtout ne fait pas — ce panneau. */}
          <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.6, marginTop: 0, marginBottom: 18 }}>
            {t('retour_intro')}
          </p>

          {confirme && (
            <div style={{ marginBottom: 14, padding: '10px 12px', borderRadius: 8,
                          background: '#f0fdf4', border: '1px solid #86efac',
                          color: '#16a34a', fontSize: 13, fontWeight: 600 }}>
              {t('retour_sent')}
            </div>
          )}

          {chargement ? (
            <p style={{ color: '#6b7280', fontSize: 13 }}>{t('retour_loading')}</p>
          ) : (
            <>
              {positions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '18px 0', color: '#9ca3af', fontSize: 13 }}>
                  <div>{t('retour_empty')}</div>
                  <div style={{ marginTop: 6, fontSize: 12 }}>{t('retour_empty_hint')}</div>
                </div>
              ) : positions.map(p => {
                const teinte = TEINTE_POSITION[p.position] || TEINTE_POSITION.RESERVE;
                const deja = retourDe(p);
                const identifiant = cle(p);
                return (
                  <div key={identifiant}
                       style={{ marginBottom: 14, padding: '12px 14px', borderRadius: 10,
                                border: `1px solid ${teinte.border}`, background: teinte.bg }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>
                          R{p.rule_number} — {p.rule_name}
                        </div>
                        <div style={{ fontSize: 11, color: teinte.color, fontWeight: 700, marginTop: 2 }}>
                          {t('retour_rendered_label')} : {{
                            FAVORABLE:   t('retour_pos_favorable'),
                            RESERVE:     t('retour_pos_reserve'),
                            DEFAVORABLE: t('retour_pos_defavorable'),
                          }[p.position] || p.position}
                        </div>
                      </div>
                      {deja && (
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', flexShrink: 0 }}>
                          {t('retour_flagged')}
                        </span>
                      )}
                    </div>

                    {/* Le MOTIF de la position. Sans lui, on demanderait de juger
                        une conclusion sans montrer le raisonnement — reproche
                        exact de Marc à la feuille S0 le 21/08/2026.
                        ⚠️ Vide pour les analyses antérieures à la révision 0011 :
                        le détail n'était alors persisté nulle part. */}
                    {p.details?.length > 0 && (
                      <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12,
                                   color: '#374151', lineHeight: 1.6 }}>
                        {p.details.map((d, i) => <li key={i}>{d}</li>)}
                      </ul>
                    )}

                    {ouvert === identifiant ? (
                      <Formulaire
                        avecPosition
                        positionRendue={p.position}
                        placeholder={t('retour_why_ph')}
                        choix={choix} setChoix={setChoix}
                        motif={motif} setMotif={setMotif}
                        erreur={erreur} envoi={envoi}
                        onAnnuler={fermerFormulaire}
                        onEnvoyer={() => envoyer({
                          kind: 'position',
                          norme: p.norme,
                          rule_number: p.rule_number,
                          position_attendue: choix,
                        })}
                      />
                    ) : (
                      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
                        <button
                          onClick={() => ouvrirFormulaire(identifiant, deja)}
                          style={{ padding: '5px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                                   background: '#fff', border: '1px solid #d1d5db', color: 'var(--black)' }}
                        >
                          {deja ? t('retour_edit') : t('retour_flag')}
                        </button>
                        {deja && (
                          <button
                            onClick={() => retirer(deja.id)}
                            style={{ background: 'none', border: 'none', fontSize: 12,
                                     color: '#dc2626', cursor: 'pointer', textDecoration: 'underline' }}
                          >
                            {t('retour_delete')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* ── Ce que Normascan n'a PAS vu ──
                  Aucune règle ne peut le produire : c'est la seule source du
                  RAPPEL, et donc la seule mesure du faux favorable — celui qui,
                  dans le monde réel, est la faute grave. */}
              <div style={{ marginTop: 22, paddingTop: 18, borderTop: '1px dashed #d1d5db' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>
                  {t('retour_missing_title')}
                </div>
                <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0' }}>
                  {t('retour_missing_hint')}
                </p>

                {ecartsManques.length > 0 && (
                  <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 12, color: '#374151' }}>
                    {ecartsManques.map(r => (
                      <li key={r.id} style={{ marginBottom: 4 }}>
                        {r.commentaire}{' '}
                        <button
                          onClick={() => retirer(r.id)}
                          style={{ background: 'none', border: 'none', fontSize: 11,
                                   color: '#dc2626', cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          {t('retour_delete')}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {ouvert === 'ecart_manque' ? (
                  <Formulaire
                    avecPosition={false}
                    placeholder={t('retour_missing_ph')}
                    choix={choix} setChoix={setChoix}
                    motif={motif} setMotif={setMotif}
                    erreur={erreur} envoi={envoi}
                    onAnnuler={fermerFormulaire}
                    onEnvoyer={() => envoyer({ kind: 'ecart_manque' })}
                  />
                ) : (
                  <button
                    onClick={() => ouvrirFormulaire('ecart_manque', null)}
                    style={{ marginTop: 10, padding: '5px 12px', borderRadius: 6, fontSize: 12,
                             cursor: 'pointer', background: '#fff', border: '1px solid #d1d5db',
                             color: 'var(--black)' }}
                  >
                    {t('retour_missing_send')}
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', textAlign: 'right' }}>
          <Button variant="outline" size="sm" onClick={onClose}>{t('retour_close')}</Button>
        </div>
      </div>
    </div>
  );
};

export default RetourAvisPanel;
